/**
 * PageSEO
 *
 * Drop this at the top of any public page to override the default title
 * and meta description. Requires HelmetProvider to be present in main.tsx.
 *
 * Usage:
 *   <PageSEO
 *     title="Pricing | MomentSent"
 *     description="Plans starting at $7.99/month. 14-day free trial, no credit card required."
 *     canonical="https://momentsent.com/pricing"
 *   />
 *
 * The `structuredData` prop accepts any valid JSON-LD object and injects it
 * as a <script type="application/ld+json"> tag for Google's rich results.
 */

import { Helmet } from 'react-helmet-async';

interface PageSEOProps {
  title:          string;
  description:    string;
  canonical?:     string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = 'https://momentsent.com';
const OG_IMAGE = `${BASE_URL}/og-image.png`;

export default function PageSEO({ title, description, canonical, structuredData }: PageSEOProps) {
  const canonicalUrl = canonical ?? BASE_URL;

  return (
    <Helmet>
      {/* Core */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title"       content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:image"       content={OG_IMAGE} />
      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content="MomentSent" />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
